// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import {
  Card,
  CardContent,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import uniq from 'lodash/uniq'
import React from 'react'
import DeleteButton from '../../common/iconButtons/DeleteButton'
import DoneButton from '../../common/iconButtons/DoneButton'
import NoteButton from '../../common/iconButtons/NoteButton'
import type { UserType } from '../admin/users/userTypes'
import { i18n } from '../index'
import type { ShiftResponsibility } from '../shiftResponsibility/shiftResponsibilityTypes'
import css from './EmployeeCard.css'
import { positionOptions } from './StaffingPlan'
import type { StaffMember } from './types'

type EmployeeCardProps = {
  idx: number
  employee: StaffMember
  removeStaffMember: (...args: Array<any>) => any
  isEditing?: boolean
  valueChanged: (arg0: number, arg1: string, arg2: string | Array<string>) => void
  toggleEditNote: (...args: Array<any>) => any
  editNote: boolean
  isToday: boolean
  shiftResponsibilities: Array<ShiftResponsibility>
  userType: UserType
  activityDescription: string
  openEmployeeRowDialog: (...args: Array<any>) => any
  submit: (...args: Array<any>) => any
  employeeIncentives: {
    sia: number
    sit: number
    tua: number
    tut: number
  }
}

const EmployeeCard = ({
  idx,
  employee,
  removeStaffMember,
  isEditing,
  valueChanged,
  toggleEditNote,
  editNote,
  isToday,
  shiftResponsibilities,
  userType,
  activityDescription,
  openEmployeeRowDialog,
  submit,
  employeeIncentives,
}: EmployeeCardProps) => {
  const {
    salesIncentiveActual,
    salesIncentiveTarget,
    thumbsUpActual,
    thumbsUpTarget,
    user,
    startTime,
    endTime,
    position,
    responsibility,
    note,
    breaks,
  } = employee

  // responsibilities with the employee's currently saved ones, minus the 'Other' sentinel.
  const responsibilityOptions = uniq([...shiftResponsibilities.map((s) => s.name), ...responsibility])
    .filter((s) => s !== 'Other')
    .sort()

  const selectedResponsibilities = responsibility.filter((r) => r !== 'Other')
  const isUserRole = userType === 'User'
  const salesIncentiveTargetReachedClass =
    salesIncentiveActual >= salesIncentiveTarget ? css.targetReached : css.targetNotReached
  const thumbsUpTargetReachedClass = thumbsUpActual >= thumbsUpTarget ? css.targetReached : css.targetNotReached

  const translateResponsibility = (r: string) => i18n.t([`staffingPlan.responsibilities.${r}`, r])

  const showIncentives = salesIncentiveTarget !== undefined && userType !== 'User'
  const showThumbsUp = thumbsUpActual !== undefined && userType !== 'User'

  const renderField = (label: string, content: React.ReactNode) => (
    <div className={css.field}>
      <span className={css.fieldLabel}>{label}</span>
      <div className={css.fieldValue}>{content}</div>
    </div>
  )

  // Full-width field whose value is stacked under the label (used for Vastuu,
  // whose bulleted list can be tall).
  const renderStackedField = (label: string, content: React.ReactNode) => (
    <div className={css.stackedField}>
      <span className={css.fieldLabel}>{label}</span>
      <div className={css.fieldValue}>{content}</div>
    </div>
  )

  const renderResponsibilityValue = () => {
    if (selectedResponsibilities.length === 0) return null
    return (
      <ul className={css.responsibilityBulletList}>
        {selectedResponsibilities.map((r) => (
          <li key={r}>{translateResponsibility(r)}</li>
        ))}
      </ul>
    )
  }

  const renderTime = () =>
    !isUserRole && isEditing && isToday ? (
      <Stack direction='row' alignItems='center' className={css.timeStack}>
        <TextField
          id={`startTime-mobile-${employee.userId}-${idx}`}
          type='time'
          className={css.timeInput}
          defaultValue={startTime}
          onChange={(e) => valueChanged(idx, 'startTime', e.target.value)}
          inputProps={{ step: 300 }}
        />
        <span className={css.timeSeparator}>–</span>
        <TextField
          id={`endTime-mobile-${employee.userId}-${idx}`}
          type='time'
          className={css.timeInput}
          defaultValue={endTime}
          onChange={(e) => valueChanged(idx, 'endTime', e.target.value)}
          inputProps={{ step: 300 }}
        />
      </Stack>
    ) : (
      <span>
        {startTime} – {endTime}
      </span>
    )

  const renderPosition = () =>
    !isUserRole && isEditing && isToday ? (
      <FormControl fullWidth>
        <Select
          fullWidth
          onChange={(e) => valueChanged(idx, 'position', e.target.value)}
          value={position}
          inputProps={{
            'aria-label': i18n.t('staffingPlan.position'),
            id: `position-mobile-${employee.userId}-${idx}`,
          }}
        >
          {positionOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {i18n.t(`staffingPlan.positions.${option.label}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : (
      <span className={css.positionTxt} onClick={() => openEmployeeRowDialog(activityDescription)}>
        {i18n.t(`staffingPlan.positions.${position}`)}
      </span>
    )

  const renderResponsibility = () =>
    !isUserRole && isEditing ? (
      <FormControl fullWidth>
        <Select
          fullWidth
          multiple
          displayEmpty
          onChange={(e) => valueChanged(idx, 'responsibility', e.target.value)}
          value={selectedResponsibilities}
          MenuProps={{
            PaperProps: {
              sx: {
                '& .MuiListItemText-primary': {
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                },
              },
            },
          }}
          renderValue={(selected) => {
            const names = selected.filter((r) => r !== 'Other')
            if (names.length === 0) return ''
            return (
              <span className={css.responsibilityList}>
                {names.map((r) => (
                  <span key={r} className={css.responsibilityListItem}>
                    {translateResponsibility(r)}
                  </span>
                ))}
              </span>
            )
          }}
          inputProps={{
            'aria-label': i18n.t('staffingPlan.responsibility'),
            id: `responsibility-mobile-${employee.userId}-${idx}`,
          }}
        >
          {responsibilityOptions.map((option) => (
            <MenuItem key={option} value={option}>
              <Checkbox
                checked={selectedResponsibilities.includes(option)}
                aria-hidden='true'
                tabIndex={-1}
                disableRipple
                size='small'
                className={css.responsibilityCheckbox}
              />
              <ListItemText primary={translateResponsibility(option)} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : (
      renderResponsibilityValue()
    )

  return (
    <Card component='li' className={css.card} elevation={1}>
      <CardContent className={css.cardContent}>
        <div className={css.cardHeader}>
          <Typography variant='subtitle1' className={css.name}>
            {user.name}
          </Typography>
          {isEditing && !isUserRole && isToday && (
            <DeleteButton onClick={() => removeStaffMember(employee.userId, idx)} />
          )}
        </div>

        {renderField(i18n.t('staffingPlan.time'), renderTime())}
        {renderField(i18n.t('staffingPlan.position'), renderPosition())}
        {renderStackedField(i18n.t('staffingPlan.responsibility'), renderResponsibility())}

        {showIncentives &&
          renderField(
            i18n.t('staffingPlan.incentiveSales'),
            !isUserRole && isEditing ? (
              <TextField
                className={css.numberInput}
                id={`salesIncentiveTarget-mobile-${employee.userId}-${idx}`}
                placeholder='0'
                type='number'
                value={salesIncentiveTarget || ''}
                onChange={(e) => valueChanged(idx, 'salesIncentiveTarget', e.target.value)}
              />
            ) : (
              <span className={salesIncentiveTargetReachedClass}>
                {`${employeeIncentives.sia} / ${employeeIncentives.sit || 0}`}
              </span>
            ),
          )}

        {showThumbsUp &&
          renderField(
            i18n.t('staffingPlan.thumbsUpServices'),
            !isUserRole && isEditing ? (
              <TextField
                className={css.numberInput}
                id={`thumbsUpTarget-mobile-${employee.userId}-${idx}`}
                type='number'
                placeholder='0'
                value={thumbsUpTarget || ''}
                onChange={(e) => valueChanged(idx, 'thumbsUpTarget', e.target.value)}
              />
            ) : (
              <span className={thumbsUpTargetReachedClass}>
                {`${employeeIncentives.tua} / ${employeeIncentives.tut || 0}`}
              </span>
            ),
          )}

        {isEditing && (
          <div className={css.cardActions}>
            <NoteButton onClick={toggleEditNote} />
          </div>
        )}

        {editNote && isEditing ? (
          <div className={css.noteEdit}>
            <TextField
              id={`userNote-mobile-${employee.userId}-${idx}`}
              className={css.noteInput}
              value={note}
              onChange={(e) => valueChanged(idx, 'note', e.target.value)}
              label={i18n.t('staffingPlan.addNote')}
              margin='normal'
              variant='outlined'
              multiline
              autoFocus
              maxRows='3'
              fullWidth
            />
            <span className={css.noteActions}>
              <DoneButton onClick={toggleEditNote} />
              <DeleteButton
                onClick={() => {
                  valueChanged(idx, 'note', '')
                  toggleEditNote()
                }}
              />
            </span>
          </div>
        ) : (
          note && note !== '' && <div className={css.note}>{note}</div>
        )}
      </CardContent>
    </Card>
  )
}

export default EmployeeCard
